import type {
  DesignSpecFieldChange,
  DesignSpecFieldValue,
  RenderedDesignSpec,
} from "@/types/generatedImage";
import type { DesignSpec } from "@/types/designSpec";
import { designSpecSchema } from "@/types/designSpec";

function deepFreeze<T>(value: T): T {
  if (!value || typeof value !== "object" || Object.isFrozen(value)) {
    return value;
  }

  Object.freeze(value);
  for (const child of Object.values(value as Record<string, unknown>)) {
    deepFreeze(child);
  }
  return value;
}

/**
 * Validates, clones, and freezes a render contract before it crosses an async
 * generation boundary. The clone prevents later edits to the conversational
 * brief or form from changing the image's provenance retroactively.
 */
export function createDesignSpecSnapshot(
  value: DesignSpec | null | undefined
): RenderedDesignSpec {
  if (!value) return null;
  // A caller-authored render contract must fail closed. Silently dropping an
  // invalid spec here would let the renderer fall back to mutable legacy form
  // fields and leave the resulting image with no truthful provenance.
  return deepFreeze(designSpecSchema.parse(value));
}

/**
 * Rehydrates a persisted snapshot. Invalid/legacy data stays unknown instead
 * of being reconstructed from today's mutable form.
 */
export function parseDesignSpecSnapshot(value: unknown): RenderedDesignSpec {
  if (!value) return null;
  const parsed = designSpecSchema.safeParse(value);
  if (!parsed.success) return null;
  return deepFreeze(parsed.data);
}

const DESIGN_FIELD_PATHS = [
  "occasion",
  "eventDate",
  "wearerContext",
  "visual.prompt",
  "visual.primaryColor",
  "visual.accentColors",
  "visual.length",
  "visual.sleeve",
  "visual.sleeveAddons",
  "visual.neckline",
  "visual.waistline",
  "visual.silhouette",
  "visual.fabric",
  "visual.mood",
  "visual.details",
  "visual.backPrompt",
  "visual.backStyle",
  "visual.closure",
  "constraints.mustHave",
  "constraints.avoid",
  "constraints.comfort",
  "constraints.mobility",
  "constraints.bodyConcerns",
  "constraints.modesty",
  "constraints.coverage",
  "constraints.support",
  "constraints.opacity",
  "constraints.accessibility",
  "constraints.cultural",
  "constraints.construction",
  "constraints.budget",
  "references",
  "referenceGuidance",
] as const;

function fieldValue(
  spec: DesignSpec,
  path: (typeof DESIGN_FIELD_PATHS)[number]
): DesignSpecFieldValue {
  const segments = path.split(".");
  let value: unknown = spec;
  for (const segment of segments) {
    if (!value || typeof value !== "object") return undefined;
    value = (value as Record<string, unknown>)[segment];
  }
  return value as DesignSpecFieldValue;
}

function equalFieldValue(
  left: DesignSpecFieldValue,
  right: DesignSpecFieldValue
): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

/**
 * Returns only render-affecting fields whose canonical values changed.
 * Provenance metadata is deliberately excluded: it may change without asking
 * the image model to render anything differently.
 */
export function diffDesignSpecFields(
  renderedSpec: DesignSpec,
  draftSpec: DesignSpec
): DesignSpecFieldChange[] {
  return DESIGN_FIELD_PATHS.flatMap(path => {
    const renderedValue = fieldValue(renderedSpec, path);
    const draftValue = fieldValue(draftSpec, path);
    return equalFieldValue(renderedValue, draftValue)
      ? []
      : [{ path, renderedValue, draftValue }];
  });
}

export function getActiveRenderedDesignSpec(
  images: {
    front: { designSpec: RenderedDesignSpec };
    back: { designSpec: RenderedDesignSpec };
  },
  side: "front" | "back"
): RenderedDesignSpec {
  return images[side].designSpec;
}
